/**
 * Command registry: every user-facing action with its label and keyboard shortcut.
 * Keyboard handling, menus and the command palette all dispatch through here.
 */
import {
  addMarker,
  addTrack,
  addTransition,
  clipEnd,
  cloneClip,
  createClipFromMedia,
  createSolidClip,
  createTextClip,
  deleteClips,
  duplicateClips,
  editPoints,
  findClip,
  groupClips,
  insertClip,
  moveClips,
  razor,
  removeTransition,
  secondsToFrame,
  sequenceDuration,
  ungroupClip,
  updateClip,
  type Clip,
  type Id,
  type Project,
  type Track,
} from '@ve/core';
import { activeSequenceId, edit, getProject, redo, undo, useEditor } from '../state/editor-store.ts';
import { transport } from '../state/playback.ts';
import { activeSourceEngine, closeSourcePreview, useSourcePreview } from '../state/source-preview.ts';
import { saveNow } from '../state/session.ts';
import { toast } from '../state/toast-store.ts';
import { useUi } from '../state/ui-store.ts';

export interface Command {
  id: string;
  label: string;
  /** e.g. "Mod+Z", "Shift+Delete", "Space". `Mod` = ⌘ on macOS, Ctrl elsewhere. */
  shortcut?: string | string[];
  run(): void;
  /** Allow the shortcut while typing in a text field. */
  global?: boolean;
}

let clipboard: Array<{ clip: Clip; trackIndex: number; kind: Track['kind'] }> = [];

function selectedClipIds(): Id[] {
  return useEditor.getState().selection.clipIds;
}

function requireSelection(): Id[] | null {
  const ids = selectedClipIds();
  if (ids.length === 0) {
    toast.info('Select one or more clips first');
    return null;
  }
  return ids;
}

function activeSequence() {
  const p = getProject();
  return p.sequences[activeSequenceId()]!;
}

/** Finds the first video track with free space at [start, start+duration), or adds one. */
function freeVideoTrack(start: number, duration: number): Id {
  const seq = activeSequence();
  const free = [...seq.videoTracks]
    .reverse()
    .find((t) => !t.locked && t.clips.every((c) => clipEnd(c) <= start || c.start >= start + duration));
  if (free) return free.id;
  return edit('Add track', (p) => addTrack(p, seq.id, 'video'))!;
}

export function splitAtPlayhead(): void {
  const frame = transport.frame;
  const sel = selectedClipIds();
  const seq = activeSequence();
  // With a selection, cut only the tracks containing selected clips; otherwise cut everything.
  const trackIds = sel.length
    ? [...seq.videoTracks, ...seq.audioTracks]
        .filter((t) => t.clips.some((c) => sel.includes(c.id)))
        .map((t) => t.id)
    : undefined;
  const created = edit('Cut', (p) => razor(p, seq.id, frame, trackIds));
  if (created && created.length === 0) toast.info('Nothing to cut at the playhead');
}

export function deleteSelection(ripple: boolean): void {
  const { selection } = useEditor.getState();
  if (selection.transitionId) {
    const id = selection.transitionId;
    edit('Delete transition', (p) => removeTransition(p, id));
    return;
  }
  const ids = requireSelection();
  if (!ids) return;
  edit(ripple ? 'Ripple delete' : 'Delete', (p) => deleteClips(p, ids, { ripple }));
}

export function insertMedia(
  mediaId: Id,
  at?: { trackId: Id; frame: number },
  mode: 'overwrite' | 'insert' = 'overwrite',
) {
  const p = getProject();
  const asset = p.media[mediaId];
  if (!asset) return;
  closeSourcePreview(); // show the timeline the clip is added to
  const seq = activeSequence();
  const frame = at?.frame ?? transport.frame;
  const clip = createClipFromMedia(asset, p.settings, frame);
  let trackId = at?.trackId;
  if (!trackId) {
    trackId =
      asset.kind === 'audio'
        ? seq.audioTracks.find((t) => !t.locked)?.id
        : freeVideoTrack(frame, clip.duration);
  }
  if (!trackId) return;
  const tid = trackId;
  const placed = edit(`Add ${asset.name}`, (d) => insertClip(d, tid, clip, mode));
  if (placed) useEditor.getState().select({ clipIds: [placed.id] });
}

export function addTextClip(): void {
  const p = getProject();
  const frame = transport.frame;
  const duration = secondsToFrame(4, p.settings.fps);
  const trackId = freeVideoTrack(frame, duration);
  const placed = edit('Add text', (d) =>
    insertClip(d, trackId, createTextClip(frame, duration, 'Your title')),
  );
  if (placed) useEditor.getState().select({ clipIds: [placed.id] });
}

export function addSolidClip(): void {
  const p = getProject();
  const frame = transport.frame;
  const duration = secondsToFrame(4, p.settings.fps);
  const trackId = freeVideoTrack(frame, duration);
  const placed = edit('Add solid', (d) => insertClip(d, trackId, createSolidClip(frame, duration)));
  if (placed) useEditor.getState().select({ clipIds: [placed.id] });
}

/** Adds a transition on the cut nearest to the playhead on the selected clip's track. */
export function addTransitionAtSelection(type: Parameters<typeof addTransition>[2] = 'crossfade'): void {
  const ids = requireSelection();
  if (!ids) return;
  const p = getProject();
  const loc = findClip(p, ids[0]!);
  if (!loc) return;
  const { track, clip, index } = loc;
  const prev = track.clips[index - 1];
  const next = track.clips[index + 1];
  const frame = transport.frame;
  const useStart =
    prev &&
    clipEnd(prev) === clip.start &&
    (!next || Math.abs(frame - clip.start) < Math.abs(frame - clipEnd(clip)));
  const fromId = useStart ? prev!.id : clip.id;
  const dur = secondsToFrame(1, p.settings.fps);
  const id = edit('Add transition', (d) => addTransition(d, fromId, type, dur));
  if (id) useEditor.getState().select({ transitionId: id });
}

export function copySelection(): void {
  const ids = selectedClipIds();
  if (ids.length === 0) return;
  const p = getProject();
  clipboard = ids.flatMap((id) => {
    const loc = findClip(p, id);
    if (!loc) return [];
    const list = loc.track.kind === 'video' ? loc.sequence.videoTracks : loc.sequence.audioTracks;
    return [{ clip: structuredClone(loc.clip), trackIndex: list.indexOf(loc.track), kind: loc.track.kind }];
  });
  toast.info(`Copied ${clipboard.length} clip(s)`);
}

export function paste(): void {
  if (clipboard.length === 0) return;
  const frame = transport.frame;
  const minStart = Math.min(...clipboard.map((c) => c.clip.start));
  const seqId = activeSequenceId();
  const ids = edit('Paste', (p) => {
    const seq = p.sequences[seqId]!;
    return clipboard.map(({ clip, trackIndex, kind }) => {
      const list = kind === 'video' ? seq.videoTracks : seq.audioTracks;
      while (list.length <= trackIndex) addTrack(p, seqId, kind);
      const copy = cloneClip(clip);
      copy.start = frame + (clip.start - minStart);
      return insertClip(p, list[trackIndex]!.id, copy).id;
    });
  });
  if (ids) useEditor.getState().select({ clipIds: ids });
}

export function cutSelection(): void {
  copySelection();
  deleteSelection(false);
}

export function nudgeSelection(delta: number): void {
  const ids = selectedClipIds();
  if (ids.length === 0) return;
  const p = getProject();
  const moves = ids.flatMap((id) => {
    const loc = findClip(p, id);
    return loc ? [{ clipId: id, trackId: loc.track.id, start: loc.clip.start + delta }] : [];
  });
  edit('Nudge', (d) => moveClips(d, moves), { coalesceKey: `nudge:${ids.join(',')}` });
}

export function groupSelection(): void {
  const ids = requireSelection();
  if (!ids) return;
  const p = getProject();
  const name = `Chapter ${Object.keys(p.sequences).length}`;
  const seqId = edit('Group into chapter', (d) => groupClips(d, ids, name));
  if (seqId) {
    useEditor.getState().clearSelection();
    toast.success(`Created “${name}” — double-click it to edit`);
  }
}

export function ungroupSelection(): void {
  const ids = requireSelection();
  if (!ids || ids.length !== 1) return;
  const created = edit('Ungroup', (d) => ungroupClip(d, ids[0]!));
  if (created) useEditor.getState().select({ clipIds: created });
}

export function toggleClipEnabled(): void {
  const ids = requireSelection();
  if (!ids) return;
  const p = getProject();
  const enabled = !findClip(p, ids[0]!)?.clip.enabled;
  edit(enabled ? 'Enable clips' : 'Disable clips', (d) =>
    ids.forEach((id) => updateClip(d, id, { enabled })),
  );
}

/** Clips among `ids` that carry sound (video and audio clips), i.e. can be muted. */
export function soundClips(project: Project, ids: Id[]): Array<Extract<Clip, { muted: boolean }>> {
  return ids.flatMap((id) => {
    const clip = findClip(project, id)?.clip;
    return clip && 'muted' in clip ? [clip] : [];
  });
}

export function setClipsMuted(ids: Id[], muted: boolean): void {
  edit(muted ? 'Mute clips' : 'Unmute clips', (d) => ids.forEach((id) => updateClip(d, id, { muted })));
}

/** Mutes the selected clips, or unmutes them if they are all muted already. */
export function toggleClipsMuted(): void {
  const ids = requireSelection();
  if (!ids) return;
  const clips = soundClips(getProject(), ids);
  if (clips.length === 0) {
    toast.info('None of the selected clips have sound');
    return;
  }
  setClipsMuted(
    clips.map((c) => c.id),
    !clips.every((c) => c.muted),
  );
}

function jumpToEdit(direction: 1 | -1): void {
  const points = editPoints(activeSequence());
  const f = transport.frame;
  const target = direction > 0 ? points.find((x) => x > f) : [...points].reverse().find((x) => x < f);
  if (target !== undefined) transport.seek(target);
}

function setLoopPoint(which: 'start' | 'end'): void {
  const s = useEditor.getState();
  const f = transport.frame;
  const current = s.loopRange ?? { start: 0, end: sequenceDuration(getProject(), activeSequenceId()) };
  const next =
    which === 'start'
      ? { start: f, end: Math.max(f + 1, current.end) }
      : { start: Math.min(current.start, f - 1), end: f };
  s.setLoopRange({ start: Math.max(0, next.start), end: next.end });
}

function zoomBy(factor: number): void {
  const s = useEditor.getState();
  s.setZoom(s.pxPerFrame * factor);
}

export const COMMANDS: Command[] = [
  { id: 'undo', label: 'Undo', shortcut: 'Mod+Z', run: undo },
  { id: 'redo', label: 'Redo', shortcut: ['Mod+Shift+Z', 'Mod+Y'], run: redo },
  { id: 'save', label: 'Save', shortcut: 'Mod+S', run: () => void saveNow(), global: true },
  { id: 'play', label: 'Play / Pause', shortcut: 'Space', run: transport.toggle },
  { id: 'stepBack', label: 'Previous frame', shortcut: 'ArrowLeft', run: () => transport.step(-1) },
  { id: 'stepFwd', label: 'Next frame', shortcut: 'ArrowRight', run: () => transport.step(1) },
  { id: 'stepBack10', label: 'Back 10 frames', shortcut: 'Shift+ArrowLeft', run: () => transport.step(-10) },
  {
    id: 'stepFwd10',
    label: 'Forward 10 frames',
    shortcut: 'Shift+ArrowRight',
    run: () => transport.step(10),
  },
  { id: 'prevEdit', label: 'Previous edit point', shortcut: 'ArrowUp', run: () => jumpToEdit(-1) },
  { id: 'nextEdit', label: 'Next edit point', shortcut: 'ArrowDown', run: () => jumpToEdit(1) },
  { id: 'home', label: 'Go to start', shortcut: 'Home', run: () => transport.seekViewer(0) },
  {
    id: 'end',
    label: 'Go to end',
    shortcut: 'End',
    run: () =>
      activeSourceEngine()
        ? transport.seekViewer(Infinity)
        : transport.seek(sequenceDuration(getProject(), activeSequenceId())),
  },
  { id: 'split', label: 'Cut at playhead', shortcut: ['Mod+K', 'S'], run: splitAtPlayhead },
  { id: 'delete', label: 'Delete', shortcut: ['Delete', 'Backspace'], run: () => deleteSelection(false) },
  {
    id: 'rippleDelete',
    label: 'Ripple delete',
    shortcut: ['Shift+Delete', 'Shift+Backspace'],
    run: () => deleteSelection(true),
  },
  { id: 'copy', label: 'Copy', shortcut: 'Mod+C', run: copySelection },
  { id: 'cut', label: 'Cut to clipboard', shortcut: 'Mod+X', run: cutSelection },
  { id: 'paste', label: 'Paste at playhead', shortcut: 'Mod+V', run: paste },
  {
    id: 'duplicate',
    label: 'Duplicate',
    shortcut: 'Mod+D',
    run: () => {
      const ids = requireSelection();
      if (!ids) return;
      const created = edit('Duplicate', (p) => duplicateClips(p, ids));
      if (created) useEditor.getState().select({ clipIds: created });
    },
  },
  { id: 'nudgeLeft', label: 'Nudge left', shortcut: 'Alt+ArrowLeft', run: () => nudgeSelection(-1) },
  { id: 'nudgeRight', label: 'Nudge right', shortcut: 'Alt+ArrowRight', run: () => nudgeSelection(1) },
  { id: 'group', label: 'Group into chapter', shortcut: 'Mod+G', run: groupSelection },
  { id: 'ungroup', label: 'Ungroup chapter', shortcut: 'Mod+Shift+G', run: ungroupSelection },
  { id: 'toggleEnabled', label: 'Enable / disable clip', shortcut: 'Shift+E', run: toggleClipEnabled },
  { id: 'toggleMuted', label: 'Mute / unmute clips', shortcut: 'Shift+M', run: toggleClipsMuted },
  {
    id: 'transition',
    label: 'Add crossfade',
    shortcut: 'Mod+T',
    run: () => addTransitionAtSelection('crossfade'),
  },
  { id: 'text', label: 'Add text', shortcut: 'T', run: addTextClip },
  { id: 'solid', label: 'Add color solid', run: addSolidClip },
  {
    id: 'marker',
    label: 'Add marker',
    shortcut: 'M',
    run: () => edit('Add marker', (p) => addMarker(p, activeSequenceId(), transport.frame)),
  },
  {
    id: 'toolSelect',
    label: 'Selection tool',
    shortcut: 'V',
    run: () => useEditor.getState().setTool('select'),
  },
  { id: 'toolRazor', label: 'Razor tool', shortcut: 'C', run: () => useEditor.getState().setTool('razor') },
  {
    id: 'toolRipple',
    label: 'Ripple edit tool',
    shortcut: 'B',
    run: () => useEditor.getState().setTool('ripple'),
  },
  {
    id: 'toolRoll',
    label: 'Rolling edit tool',
    shortcut: 'N',
    run: () => useEditor.getState().setTool('roll'),
  },
  { id: 'toolSlip', label: 'Slip tool', shortcut: 'Y', run: () => useEditor.getState().setTool('slip') },
  {
    id: 'snapping',
    label: 'Toggle snapping',
    shortcut: 'Shift+S',
    run: () => useEditor.getState().setSnapping(!useEditor.getState().snapping),
  },
  { id: 'zoomIn', label: 'Zoom in', shortcut: ['=', '+'], run: () => zoomBy(1.5) },
  { id: 'zoomOut', label: 'Zoom out', shortcut: '-', run: () => zoomBy(1 / 1.5) },
  {
    id: 'zoomFit',
    label: 'Zoom to fit',
    shortcut: 'Shift+Z',
    run: () => useUi.getState().requestZoomToFit(),
  },
  { id: 'loopIn', label: 'Set loop in', shortcut: 'I', run: () => setLoopPoint('start') },
  { id: 'loopOut', label: 'Set loop out', shortcut: 'O', run: () => setLoopPoint('end') },
  {
    id: 'loopToggle',
    label: 'Toggle loop playback',
    shortcut: 'L',
    run: () => useEditor.getState().setLoopEnabled(!useEditor.getState().loopEnabled),
  },
  {
    id: 'selectAll',
    label: 'Select all clips',
    shortcut: 'Mod+A',
    run: () => {
      const seq = activeSequence();
      const ids = [...seq.videoTracks, ...seq.audioTracks]
        .filter((t) => !t.locked)
        .flatMap((t) => t.clips.map((c) => c.id));
      useEditor.getState().select({ clipIds: ids });
    },
  },
  {
    id: 'deselect',
    label: 'Deselect / back to timeline',
    shortcut: 'Escape',
    run: () => {
      // Leaves a library preview in the viewer first, otherwise clears the selection.
      if (useSourcePreview.getState().mediaId !== null) closeSourcePreview();
      else useEditor.getState().clearSelection();
    },
  },
  {
    id: 'export',
    label: 'Export…',
    shortcut: 'Mod+E',
    run: () => useUi.getState().setDialog('export'),
    global: true,
  },
  {
    id: 'palette',
    label: 'Command palette',
    shortcut: 'Mod+P',
    run: () => useUi.getState().setDialog('palette'),
    global: true,
  },
  {
    id: 'shortcuts',
    label: 'Keyboard shortcuts',
    shortcut: '?',
    run: () => useUi.getState().setDialog('shortcuts'),
  },
];

/** The first shortcut of a command, formatted for display. */
export function shortcutOf(id: string): string | undefined {
  const s = COMMANDS.find((c) => c.id === id)?.shortcut;
  const first = Array.isArray(s) ? s[0] : s;
  return first ? formatShortcut(first) : undefined;
}

export function formatShortcut(shortcut: string): string {
  const mac = navigator.platform.toLowerCase().includes('mac');
  return shortcut
    .split('+')
    .map((k) => {
      if (k === 'Mod') return mac ? '⌘' : 'Ctrl';
      if (k === 'Shift') return mac ? '⇧' : 'Shift';
      if (k === 'Alt') return mac ? '⌥' : 'Alt';
      if (k.startsWith('Arrow'))
        return { ArrowLeft: '←', ArrowRight: '→', ArrowUp: '↑', ArrowDown: '↓' }[k] ?? k;
      return k;
    })
    .join(mac ? '' : '+');
}
